import { Button } from "@hoylu/client-common";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { QUICK_ACCESS_CATEGORY_ID } from "../services/templates/types";
import { fetchTemplates } from "../state/templates/templates.actions";
import { quickAccessTemplates as selectQuickAccessTemplates } from "../state/templates/templates.selectors";
import type { TemplateMetadata } from "../state/templates/types";
import {
  isPasswordRequired,
  selectUserName,
} from "../state/user/user.selector";
import {
  activateDashboardOption,
  createWorkspace,
  createWorkspaceFromTemplateId,
} from "../state/workspaces/workspaces.actions";
import { getCreateBlankWorkspacePayload } from "../utils/create.workspace.utils";
import { useWorkspaceDefaultImage } from "../utils/defaultImages";
import { defaultModule } from "../utils/defaultModule";
import { useI18n } from "../utils/hooks/use.i18n";
import Styles from "./CreateWorkspaceSection.module.css";
import { TemplateCard } from "./cards/template.card";
import SliderLayout from "./layouts/SliderLayout";

const seeMoreBtnStyles = {
  fontWeight: "700",
  backgroundColor: "transparent",
  color: "var(--global--accent-deep--Color)",
  width: "fit-content",
  padding: 0,
  margin: 0,
};

export const CreateWorkspaceSection: React.FC = () => {
  const dispatch = useDispatch();

  const userName = useSelector(selectUserName);
  const hasToSetPassword = useSelector(isPasswordRequired);
  const quickAccessTemplates = useSelector(selectQuickAccessTemplates);

  const t = useI18n("SECTIONS.");
  const tcw = useI18n("CREATE_WORKSPACES_SECTION.");
  const defaultImgSrc = useWorkspaceDefaultImage("HOYLU");

  const blankWorkspaceTemplate = {
    templateId: "",
    workspaceId: "",
    name: defaultModule,
  };

  const allDashboardTemplates: TemplateMetadata[] = [
    blankWorkspaceTemplate,
    ...quickAccessTemplates,
  ];

  useEffect(() => {
    dispatch(fetchTemplates.request({ categoryId: QUICK_ACCESS_CATEGORY_ID }));
  }, [dispatch]);

  const createFromTemplate = (templateId: string) => {
    dispatch(createWorkspaceFromTemplateId.request({ templateId }));
  };

  const createBlankWorkspace = () => {
    if (hasToSetPassword) {
      dispatch(
        activateDashboardOption({
          optionType: DashboardOption.CREATE,
        }),
      );
    } else {
      dispatch(createWorkspace.request(getCreateBlankWorkspacePayload()));
    }
  };

  const openAssetPanel = () => {
    dispatch(
      activateDashboardOption({
        optionType: DashboardOption.ASSET_PANEL,
      }),
    );
  };

  return (
    <div className={Styles.createWorkspaceSection}>
      <div className={Styles.greetingsContainer}>
        <span className={Styles.greetings}>
          {t("START_FROM_TEMPLATE.TITLE", userName)}
        </span>
        <Button additionalStyles={seeMoreBtnStyles} onClick={openAssetPanel}>
          {t("START_FROM_TEMPLATE.BUTTON")} <span>&gt;</span>
        </Button>
      </div>
      <SliderLayout>
        {allDashboardTemplates.map((t) => (
          <TemplateCard
            enabled
            imageURL={t.name === defaultModule ? "" : defaultImgSrc}
            isDefaultModule={t.name === defaultModule}
            key={t.templateId}
            onClick={() =>
              t.templateId && t.name !== defaultModule
                ? createFromTemplate(t.templateId)
                : createBlankWorkspace()
            }
            templateName={
              t.name === defaultModule ? tcw("MODULES.BLANK_WORKSPACE") : t.name
            }
            tooltip={tcw("CREATE")}
            workspaceId={t.workspaceId}
          />
        ))}
      </SliderLayout>
    </div>
  );
};
